import { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { sort } from 'fast-sort';
import {
  EstateInputDataFormatType,
  EstateInputDataInstance_FieldsFragment,
  EstateWorkItem,
  useDocumentGeneration_GenerateDocumentForEstateWorkItemMutation,
  useDocumentGenerationTemplate_GetAllDocumentGenerationTemplateSelectListItemsQuery,
} from 'queries';

import AutocompleteFormSelectField from 'common/autocomplete-form-select-field';
import { Button } from 'common/button';
import Spinner from 'common/spinner';
import IncompleteFieldsPromptModal from 'pages/template-data-wizard-page/components/incomplete-fields-prompt-modal';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
  },
  button: {
    padding: '10px',
  },
}));

type EstateWorkItemGenerateDocumentProps = {
  estateWorkItem: EstateWorkItem;
  estateInputDataInstances: EstateInputDataInstance_FieldsFragment[];
  refetchEstateWorkItems: () => void;
};

const EstateWorkItemGenerateDocument = ({
  estateWorkItem,
  estateInputDataInstances,
  refetchEstateWorkItems,
}: EstateWorkItemGenerateDocumentProps) => {
  const classes = useStyles();

  const [showIncompleteFieldsPrompt, setShowIncompleteFieldsPrompt] = useState(false);
  const [selectedDocumentGenerationTemplateId, setSelectedDocumentGenerationTemplateId] =
    useState<string>();
  const [toGenerateDocumentGenerationTemplateId, setToGenerateDocumentGenerationTemplateId] =
    useState<string>();

  const { data: documentGenerationTemplatesData, loading: documentGenerationTemplatesLoading } =
    useDocumentGenerationTemplate_GetAllDocumentGenerationTemplateSelectListItemsQuery({
      onError: (e) => {
        showAlert({
          message: 'Error getting all document generation templates: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
      fetchPolicy: 'no-cache',
      nextFetchPolicy: 'no-cache',
    });

  const [
    generateDocumentForEstateWorkItem,
    { loading: generateDocumentForEstateWorkItemExecuting },
  ] = useDocumentGeneration_GenerateDocumentForEstateWorkItemMutation({
    onCompleted: () => {
      refetchEstateWorkItems();

      showToast({
        message: 'Document generated successfully!',
        severity: NotificationSeverity.SUCCESS,
      });
    },
    onError: (e) => {
      showAlert({
        message: 'Error generating document for estate work item: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (documentGenerationTemplatesLoading || !documentGenerationTemplatesData) {
    return <Spinner />;
  }

  const handleGenerateFromDirectTemplate = () => {
    if (!estateWorkItem.documentGenerationTemplateId) {
      showAlert({
        message: 'Task is not directly associated to a document generation template',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    handleGenerateWithDocumentGenerationTemplateId(estateWorkItem.documentGenerationTemplateId);
  };

  const handleGenerateFromSelectedTemplate = () => {
    if (!selectedDocumentGenerationTemplateId) {
      showAlert({
        message: 'Please set a document generation template',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    handleGenerateWithDocumentGenerationTemplateId(selectedDocumentGenerationTemplateId);
  };

  const handleGenerateWithDocumentGenerationTemplateId = (documentGenerationTemplateId: string) => {
    const filteredInstances =
      estateInputDataInstances.filter(
        (instance) => instance.estateInputDataFormat.type !== EstateInputDataFormatType.Document,
      ) || [];
    const hasEmptyInstances = filteredInstances.some(
      (instance) => !instance.data && !instance.estateInputDataFormat.omitEmptyPlaceholder,
    );
    if (hasEmptyInstances) {
      setToGenerateDocumentGenerationTemplateId(documentGenerationTemplateId);
      setShowIncompleteFieldsPrompt(true);
      return;
    }

    generateDocument(true, documentGenerationTemplateId);
  };

  const generateDocument = (
    keepEmptyPlaceholders: boolean,
    documentGenerationTemplateId?: string,
  ) => {
    if (!documentGenerationTemplateId && !toGenerateDocumentGenerationTemplateId) {
      showAlert({
        message:
          '"documentGenerationTemplateId" or "toGenerateDocumentGenerationTemplateId" is required',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    generateDocumentForEstateWorkItem({
      variables: {
        input: {
          portalId: estateWorkItem.estate!.portalId,
          documentGenerationTemplateId: (documentGenerationTemplateId ??
            toGenerateDocumentGenerationTemplateId)!,
          estateWorkItemId: estateWorkItem.id,
          keepEmptyPlaceholders,
          saveToVault: true,
          updateStatuses: false,
        },
      },
    });
  };

  const documentGenerationTemplateSelectOptions = sort(
    documentGenerationTemplatesData.data.map((documentGenerationTemplate) => ({
      label: documentGenerationTemplate.label,
      value: documentGenerationTemplate.id,
    })),
  ).asc((option) => option.label);

  return (
    <div className={classes.container}>
      {showIncompleteFieldsPrompt && (
        <IncompleteFieldsPromptModal
          onClose={() => setShowIncompleteFieldsPrompt(false)}
          onGenerateDocument={generateDocument}
        />
      )}

      {estateWorkItem.documentGenerationTemplateId && (
        <>
          <Button
            disabled={generateDocumentForEstateWorkItemExecuting}
            onClick={handleGenerateFromDirectTemplate}
            size="small"
            color="info"
            className={classes.button}
          >
            Generate From Associated Template
          </Button>
          <hr />
        </>
      )}

      <AutocompleteFormSelectField
        options={documentGenerationTemplateSelectOptions}
        handleOnChange={setSelectedDocumentGenerationTemplateId}
      />

      <Button
        disabled={generateDocumentForEstateWorkItemExecuting}
        onClick={handleGenerateFromSelectedTemplate}
        size="small"
        color="info"
        className={classes.button}
      >
        Generate From Selected Template
      </Button>
    </div>
  );
};

export default EstateWorkItemGenerateDocument;
